import { isFunction } from 'lodash';
import { useCallback, useMemo } from 'react';

import { IdentityDocumentType } from '@dreryk/egabinet-patient-published-language-api';

import { assertUnreachable, Bean, BindingCreator, useTranslate, ValidationRules } from '@dreryk/react-common';
import { Select } from '@dreryk/react-components';

import { identityDocumentTranslationKeys } from '../../../translations';

type EntitlementStatementIdentityDocumetnSelectType = 'IdentityCard' | 'Passport' | 'DrivingLicense';
export const ENTITLEMENT_STATEMENT_IDENTITY_DOCUMENT_SELECT_TYPES: EntitlementStatementIdentityDocumetnSelectType[] = [
   'IdentityCard',
   'Passport',
   'DrivingLicense',
];

export type EntitlementStatementIdentityDocumentSelectType = typeof ENTITLEMENT_STATEMENT_IDENTITY_DOCUMENT_SELECT_TYPES[number];

export const getEntitlementStatementIdentityDocumentSelectType = (
   entitlementStatementIdentityDocumentType: IdentityDocumentType | undefined
): EntitlementStatementIdentityDocumentSelectType | undefined => {
   switch (entitlementStatementIdentityDocumentType) {
      case 'IDENTITY_CARD':
         return 'IdentityCard';
      case 'PASSPORT':
         return 'Passport';
      case 'DRIVING_LICENCE':
         return 'DrivingLicense';
      case 'SEAMANS_BOOK':
      case 'STUDENT_CARD':
      case undefined:
         return undefined;
      default:
         return assertUnreachable(entitlementStatementIdentityDocumentType);
   }
};

export const getEntitlementStatementIdentityDocumentType = (
   entitlementStatementIdentityDocumentSelectType: EntitlementStatementIdentityDocumentSelectType | undefined
): IdentityDocumentType | undefined => {
   switch (entitlementStatementIdentityDocumentSelectType) {
      case 'IdentityCard':
         return 'IDENTITY_CARD';
      case 'Passport':
         return 'PASSPORT';
      case 'DrivingLicense':
         return 'DRIVING_LICENCE';
      default:
         return assertUnreachable(entitlementStatementIdentityDocumentSelectType);
   }
};

export type EntitlementStatementIdentityDocumentSelectTypeVisitor = <ACCEPT>(
   visitor:
      | ((type: EntitlementStatementIdentityDocumentSelectType) => ACCEPT)
      | Record<`visit${EntitlementStatementIdentityDocumentSelectType}`, () => ACCEPT>
) => ACCEPT;

export const createEntitlementStatementIdentityDocumentSelectTypeVisitor = (
   entitlementStatementIdentityDocumentSelectType: EntitlementStatementIdentityDocumentSelectType
): EntitlementStatementIdentityDocumentSelectTypeVisitor => (visitor) => {
   if (isFunction(visitor)) {
      return visitor(entitlementStatementIdentityDocumentSelectType);
   }

   switch (entitlementStatementIdentityDocumentSelectType) {
      case 'IdentityCard':
         return visitor.visitIdentityCard();
      case 'Passport':
         return visitor.visitPassport();
      case 'DrivingLicense':
         return visitor.visitDrivingLicense();
      default:
         return assertUnreachable(entitlementStatementIdentityDocumentSelectType);
   }
};

const useTriggerLabelExtractor = (): ((type?: EntitlementStatementIdentityDocumentSelectType | null) => string) => {
   const translate = useTranslate();
   return useCallback(
      (type) => {
         return type ? translate(identityDocumentTranslationKeys.identityDocumentType.typeName[type]) : '';
      },
      [translate]
   );
};

export type OnEntitlementStatementIdentityDocumentTypeSelected = (
   type: EntitlementStatementIdentityDocumentSelectType | null
) => void;

type EntitlementStatementIdentityDocumentTypeFieldProps = {
   readonly name: string;
   readonly register: BindingCreator<Bean>;
   readonly onSelect: OnEntitlementStatementIdentityDocumentTypeSelected;
   readonly testId: string;
};

export const EntitlementStatementIdentityDocumentTypeField = (
   props: EntitlementStatementIdentityDocumentTypeFieldProps
): JSX.Element => {
   const validationRules = useMemo<ValidationRules<Bean>>(() => {
      return {
         required: true,
      };
   }, []);

   return (
      <Select<EntitlementStatementIdentityDocumentSelectType>
         testId={props.testId}
         label={identityDocumentTranslationKeys.identityDocumentType.label}
         triggerLabelExtractor={useTriggerLabelExtractor()}
         controller={props.register(props.name, validationRules)}
         defaultValue={'IdentityCard'}
         onSelect={props.onSelect}
      >
         <Select.List<EntitlementStatementIdentityDocumetnSelectType>
            items={ENTITLEMENT_STATEMENT_IDENTITY_DOCUMENT_SELECT_TYPES}
         >
            {(documentType: EntitlementStatementIdentityDocumetnSelectType) => (
               <Select.Item
                  item={documentType}
                  testId={documentType}
                  key={documentType}
                  label={identityDocumentTranslationKeys.identityDocumentType.typeName[documentType]}
               />
            )}
         </Select.List>
      </Select>
   );
};
